import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import type { Team } from '../types';
import '../styles/teams.css';

export function TeamsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [myTeams, setMyTeams] = useState<Team[]>([]);
  const [adminTeams, setAdminTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [myTeamsResponse, adminListResponse] = await Promise.all([
        api.myTeams(),
        user?.role === 'admin' ? api.listTeams() : Promise.resolve([])
      ]);
      setMyTeams(myTeamsResponse.teams || []);
      setAdminTeams(Array.isArray(adminListResponse) ? adminListResponse : []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const pendingTeams = useMemo(
    () => adminTeams.filter((team) => team.status === 'pending'),
    [adminTeams]
  );
  const activeTeams = useMemo(
    () => adminTeams.filter((team) => team.status !== 'pending'),
    [adminTeams]
  );

  const handleApprove = async (teamId: string) => {
    setApprovingId(teamId);
    try {
      await api.approveTeam(teamId);
      await loadData();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setApprovingId(null);
    }
  };

  return (
    <div className="teams-page">
      <div className="teams-page__header">
        <div>
          <h1>Teams</h1>
          <p className="hint">
            Request a new team or manage memberships. Visibility filters in the marketplace use these memberships.
          </p>
        </div>
        <div className="teams-page__actions">
          <button className="button button-secondary" onClick={() => navigate('/teams/request')}>
            Request a team
          </button>
        </div>
      </div>

      {error ? <div className="alert alert-error">{error}</div> : null}
      {loading ? <div className="loading">Loading teams…</div> : null}

      {!loading ? (
        <>
          <section className="card card-subtle">
            <div className="teams-section__header">
              <h2>My teams</h2>
              <p className="hint">Teams you belong to and can manage (if admin).</p>
            </div>
            {myTeams.length === 0 ? (
              <p className="hint">You are not a member of any teams yet.</p>
            ) : (
              <div className="teams-grid">
                {myTeams.map((team) => (
                  <div key={team.id} className="team-card">
                    <div className="team-card__header">
                      <div>
                        <h3>{team.name}</h3>
                        {team.region ? <p className="hint">{team.region}</p> : null}
                      </div>
                      <span className={`status status-${team.status === 'active' ? 'approved' : 'pending'}`}>
                        {team.status || 'pending'}
                      </span>
                    </div>
                    {team.description ? <p className="team-card__description">{team.description}</p> : null}
                    <div className="team-card__actions">
                      <Link to={`/teams/${team.id}`} className="button button-tertiary">
                        Manage
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {user?.role === 'admin' ? (
            <section className="card card-subtle">
              <div className="teams-section__header">
                <h2>Admin: all teams</h2>
                <p className="hint">Approve requests or jump into member management.</p>
              </div>
              <div className="teams-grid">
                {pendingTeams.map((team) => (
                  <div key={team.id} className="team-card team-card--pending">
                    <div className="team-card__header">
                      <div>
                        <h3>{team.name}</h3>
                        {team.region ? <p className="hint">{team.region}</p> : null}
                      </div>
                      <span className="status status-pending">pending</span>
                    </div>
                    {team.description ? <p className="team-card__description">{team.description}</p> : null}
                    <div className="team-card__actions">
                      <button
                        className="button button-secondary"
                        onClick={() => handleApprove(team.id)}
                        disabled={approvingId === team.id}
                      >
                        {approvingId === team.id ? 'Approving…' : 'Approve'}
                      </button>
                      <Link to={`/teams/${team.id}`} className="button button-tertiary">
                        View
                      </Link>
                    </div>
                  </div>
                ))}
                {activeTeams.map((team) => (
                  <div key={team.id} className="team-card">
                    <div className="team-card__header">
                      <div>
                        <h3>{team.name}</h3>
                        {team.region ? <p className="hint">{team.region}</p> : null}
                      </div>
                      <span className="status status-approved">{team.status || 'active'}</span>
                    </div>
                    {team.description ? <p className="team-card__description">{team.description}</p> : null}
                    <div className="team-card__actions">
                      <Link to={`/teams/${team.id}`} className="button button-tertiary">
                        Manage
                      </Link>
                    </div>
                  </div>
                ))}
                {adminTeams.length === 0 ? <p className="hint">No teams found.</p> : null}
              </div>
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
